import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import AssessmentsManagement from './components/AssessmentsManagement_new';
import BatchesManagement from './components/BatchesManagement';
import CenterManagement from './components/CenterManagement';
import DashboardHome from './components/DashboardHome';
import DashboardLayout from './components/DashboardLayout';
import Login from './components/Login';
import MobilisationManagement from './components/MobilisationManagement';
import MobilisationPartnerManagement from './components/MobilisationPartnerManagement';
import ReportsHome from './components/ReportsHome';
import ReportsBatchEfficiency from './components/ReportsBatchEfficiency';
import ReportsPlannedVsActual from './components/ReportsPlannedVsActual';
import ReportsPartnerPerformance from './components/ReportsPartnerPerformance';
import ReportsExceptions from './components/ReportsExceptions';
import SettingsManagement from './components/SettingsManagement_new';
import TraineeManagement from './components/TraineeManagement';
import TrainerManagement from './components/TrainerManagement';
import UserManagement from './components/UserManagement';
import { AuthProvider, useAuth } from './hooks/useAuth';
import TraineesRegistration from './components/TraineesRegistration';
import ProgramManagement from './components/ProgramsManagement';
import BatchCalendar from './components/BatchCalander';
import AttendanceManagement from './components/AttendanceManagement';
import AssessmentsAdmin from './components/AssessmentsAdmin';
import Certificates from './components/Certificates';
import MyCertificates from './components/MyCertificates';
import PlacementsManagement from './components/PlacementsManagement';
import InquiriesSupport from './components/InquiriesSupport';
import TrainerOnboarding from './components/TrainerOnboarding';
import AuditLogs from './components/AuditLogs';
import PostPlacementTracking from './components/PostPlacementTracking';
import InvoicesManagement from './components/InvoicesManagement';
import OjtScorecards from './components/OjtScorecards';
import TraineeLmsPortal from './components/TraineeLmsPortal';
import BatchConsumption from './components/BatchConsumption';
import TraineeLearningPath from './components/TraineeLearningPath';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

const RoleProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[];
}> = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user?.role || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            {/* This route needs to directly render the Login component so it can read the URL token */}
            <Route path="/reset-password" element={<Login />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* <Route path="batches" element={<BatchesManagement/>} /> */}
              <Route index element={<DashboardHome />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="trainees" element={<TraineeManagement />} />
              <Route
                path="batches"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      "SYSTEM_ADMIN",
                      "SUPER_ADMIN",
                      "OPERATIONS_ADMIN",
                      "MOBILISATION_PARTNER_ADMIN",
                      "CENTER_ADMIN",
                      "PROJECT_MANAGER",
                    ]}
                  >
                    <BatchesManagement />
                  </RoleProtectedRoute>
                }
              />
              <Route path="assessments" element={<AssessmentsManagement />} />
              <Route path="reports" element={<ReportsHome />} />
              <Route path="reports/batch-efficiency" element={<ReportsBatchEfficiency />} />
              <Route path="reports/planned-vs-actual" element={<ReportsPlannedVsActual />} />
              <Route path="reports/partner-performance" element={<ReportsPartnerPerformance />} />
              <Route path="reports/exceptions" element={<ReportsExceptions />} />
              <Route path="settings" element={<SettingsManagement />} />
              <Route path="mobilisations" element={<MobilisationManagement />} />
              <Route path="partners" element={<MobilisationPartnerManagement />} />
              <Route path="centers" element={<CenterManagement />} />
              <Route path="trainers" element={<TrainerManagement />} />
              <Route path="trainer-onboarding" element={<TrainerOnboarding />} />
              <Route path="trainees-registration" element={<TraineesRegistration />} />
              <Route path="program-management" element={<ProgramManagement />} />
              <Route path="batch-calendar" element={<BatchCalendar />} />
              <Route path="attendance" element={<AttendanceManagement />} />
              <Route path="assessments-admin" element={<AssessmentsAdmin />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="placements" element={<PlacementsManagement />} />
              <Route path="post-placement-tracking" element={<PostPlacementTracking />} />
              <Route path="my-certificates" element={<MyCertificates />} />
              <Route path="inquiries" element={<InquiriesSupport />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="invoices" element={<InvoicesManagement />} />
              <Route path="ojt-scorecards" element={<OjtScorecards />} />
              <Route path="lms-portal" element={<TraineeLmsPortal />} />
              <Route path="learning-path" element={<TraineeLearningPath />} />
              <Route path="batch-consumption" element={<BatchConsumption />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;





